import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { HelmetProvider } from "react-helmet-async";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Forums from "./pages/Forums";
import ForumPost from "./pages/ForumPost";
import Directory from "./pages/Directory";
import Events from "./pages/Events";
import Resources from "./pages/Resources";
import Messages from "./pages/Messages";
import Community from "./pages/Community";
import Feeds from "./pages/Feeds";
import SSOCallback from "./pages/SSOCallback";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import VerificationPending from "./pages/VerificationPending";
import Onboarding from "./pages/Onboarding";
import Privacy from "./pages/Privacy";
import TermsOfService from "./pages/TermsOfService";
import ContactUs from "./pages/ContactUs";
import Accessibility from "./pages/Accessibility";
import Network from "./pages/Network";
import MemberProfile from "./pages/MemberProfile";
import TranslationBanner from "./components/TranslationBanner";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    const { theme } = useTheme();
    const location = window.location.pathname;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-page)' }}>
                <div className="animate-pulse" style={{ color: 'var(--text-muted)' }}>Loading...</div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Block unverified email/password users
    if (user.is_verified === false && location !== '/verification-pending') {
        return <Navigate to="/verification-pending" replace />;
    }

    // If onboarding is not complete, forcing them to onboarding page
    // unless they are already there
    if (user.onboarding_complete === false && location !== '/onboarding') {
        return <Navigate to="/onboarding" replace />;
    }

    return children;
};

// Public Route - redirect to dashboard if already logged in
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-page)' }}>
                <div className="animate-pulse" style={{ color: 'var(--text-muted)' }}>Loading...</div>
            </div>
        );
    }

    if (user) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
            <Route path="/sso-callback" element={<PublicRoute><SSOCallback /></PublicRoute>} />
            <Route path="/verify-email" element={<PublicRoute><VerifyEmail /></PublicRoute>} />
            <Route path="/verification-pending" element={<VerificationPending />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/accessibility" element={<Accessibility />} />

            {/* Protected Routes */}
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/dashboard" element={<ProtectedRoute><Feeds /></ProtectedRoute>} />
            <Route path="/feeds" element={<Navigate to="/dashboard" replace />} />
            <Route path="/home" element={<Navigate to="/dashboard" replace />} />
            <Route path="/forums" element={<ProtectedRoute><Forums /></ProtectedRoute>} />
            <Route path="/forums/new" element={<ProtectedRoute><Forums /></ProtectedRoute>} />
            <Route path="/forums/:postId" element={<ProtectedRoute><ForumPost /></ProtectedRoute>} />
            <Route path="/directory" element={<ProtectedRoute><Directory /></ProtectedRoute>} />
            <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
            <Route path="/resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/profile/:userId" element={<ProtectedRoute><MemberProfile /></ProtectedRoute>} />
            <Route path="/network" element={<ProtectedRoute><Network /></ProtectedRoute>} />
            <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <HelmetProvider>
                <ThemeProvider>
                    <BrowserRouter>
                        <AuthProvider>
                            <TranslationBanner />
                            <AppRoutes />
                        </AuthProvider>
                    </BrowserRouter>
                </ThemeProvider>
            </HelmetProvider>
        </div>
    );
}

export default App;
